import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import SglContext from '../SglContext';
import './myAgreements.css';
import AgreementDetail from '../common/AgreementDetail';
import { useNavigate } from 'react-router-dom';

// Inline Notification Component 
const InlineNotification = ({ notification, onDismiss }) => {
  if (!notification) return null;

  const getNotificationStyles = () => {
    const typeStyles = {
      success: {
        backgroundColor: '#fdfffe',
        borderColor: '#059669',
        color: '#059669'
      },
      error: {
        backgroundColor: '#fef2f2',
        borderColor: '#dc2626',
        color: '#dc2626'
      },
      info: {
        backgroundColor: '#f0f9ff',
        borderColor: '#0369a1',
        color: '#0369a1'
      },
      warning: {
        backgroundColor: '#fffbeb',
        borderColor: '#d97706',
        color: '#d97706'
      }
    };

    return typeStyles[notification.type] || typeStyles.info;
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return 'ℹ';
    }
  };

  return (
    <div 
      className="inline-notification"
      style={{
        ...getNotificationStyles(),
        padding: '8px 16px',
        margin: '10px 16px 10px 16px',
        borderRadius: '6px',
        border: '1px solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <span style={{ fontSize: '16px', lineHeight: '1' }}>
          {getIcon()}
        </span>
        <span style={{ lineHeight: '1.4' }}>
          {notification.message}
        </span>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '10px',
            lineHeight: '1',
            boxSizing: 'border-box',
            marginTop: '-5px'
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// Notification Manager Hook
const useInlineNotifications = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info', duration = 5000) => {
    const notificationObj = { message, type, timestamp: Date.now() };
    setNotification(notificationObj);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotification(prev => 
          prev && prev.timestamp === notificationObj.timestamp ? null : prev
        );
      }, duration);
    }
    
    return notificationObj.timestamp;
  };

  const dismissNotification = () => {
    setNotification(null);
  };

  const showSuccess = (message, duration = 5000) => showNotification(message, 'success', duration);
  const showError = (message, duration = 6000) => showNotification(message, 'error', duration);
  const showInfo = (message, duration = 4000) => showNotification(message, 'info', duration);
  const showWarning = (message, duration = 5000) => showNotification(message, 'warning', duration);

  return {
    notification,
    showNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    dismissNotification
  };
};

const MyAgreements = () => {
  const [agreements, setAgreements] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending_agreements');
  const [displayAgreement, setDisplayAgreement] = useState(false)
  const [selectedAgreement, setSelectedAgreement] = useState(null)
  const tabContainerRef = useRef(null);
  const leftIndicatorRef = useRef(null);
  const rightIndicatorRef = useRef(null);
  const Sgl = useContext(SglContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 436);
  const navigate = useNavigate();

  const { 
    notification, 
    showSuccess, 
    showError, 
    showInfo, 
    showWarning, 
    dismissNotification 
  } = useInlineNotifications();

  // Map agreement types to labels
  const agreementTypeLabels = {
    signed_agreements: 'Signed Agreements',
    pending_agreements: 'Pending Agreements'
  };

  // Mobile detection useEffect
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 436);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tab scroll functionality 
  useEffect(() => {
    const updateScrollIndicators = () => {
      if (!tabContainerRef.current || !leftIndicatorRef.current || !rightIndicatorRef.current) return;
      
      const container = tabContainerRef.current;
      const isOverflowing = container.scrollWidth > container.clientWidth;
      const isAtStart = container.scrollLeft <= 1;
      const isAtEnd = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;

      if (!isOverflowing) {
        leftIndicatorRef.current.classList.remove('visible');
        rightIndicatorRef.current.classList.remove('visible');
      } else {
        leftIndicatorRef.current.classList.toggle('visible', !isAtStart);
        rightIndicatorRef.current.classList.toggle('visible', !isAtEnd);
      }
    };

    const scrollTabs = (direction) => {
      if (!tabContainerRef.current) return;
      const scrollAmount = 150;
      if (direction === 'left') {
        tabContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        tabContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };

    const handleLeftClick = () => scrollTabs('left');
    const handleRightClick = () => scrollTabs('right');

    if (leftIndicatorRef.current) {
      leftIndicatorRef.current.addEventListener('click', handleLeftClick);
    }
    if (rightIndicatorRef.current) {
      rightIndicatorRef.current.addEventListener('click', handleRightClick);
    }
    if (tabContainerRef.current) {
      tabContainerRef.current.addEventListener('scroll', updateScrollIndicators);
    }
    window.addEventListener('resize', updateScrollIndicators);

    setTimeout(updateScrollIndicators, 100);

    return () => {
      if (leftIndicatorRef.current) {
        leftIndicatorRef.current.removeEventListener('click', handleLeftClick);
      }
      if (rightIndicatorRef.current) {
        rightIndicatorRef.current.removeEventListener('click', handleRightClick);
      }
      if (tabContainerRef.current) {
        tabContainerRef.current.removeEventListener('scroll', updateScrollIndicators);
      }
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, []);

  useEffect(() => {
    loadAgreements();
  }, []);

  const loadAgreements = async (showLoadingNotification = false) => {
    try {
      setLoading(true);
      setError('');
      
      if (showLoadingNotification) {
        showInfo('Refreshing agreements...', 2000);
      }
      
      // Fetch signed agreements 
      const [signedResponse, pendingResponse] = await Promise.all([
        axios.get(`${Sgl.nestApiUrl}/billing/getSignedAgreements`, {
          params: {
            user_id: Sgl.current_user?.sgl_id,
            company_id: Sgl.company_id
          }
        }),

        axios.get(Sgl.nestApiUrl + "/billing/getUnsignAgreementsForUser", {
          params: { login_id: Sgl.current_user?.sgl_id, company_id: Sgl.company_id }
        }),

        Sgl.refreshPendingRequestsCount()
      ])

      const count = pendingResponse.data.unSignedAgreements?.length || 0;
      Sgl.setPendingAgreementsCount(count);
      
      const groupedAgreements = {
        pending_agreements: pendingResponse.data.unSignedAgreements ?? [],
        signed_agreements: signedResponse.data.success ? signedResponse.data.signedAgreements || [] : [],
      };
      
      setAgreements(groupedAgreements);
      
      // Set active tab to first available type with data
      const availableTypes = Object.keys(groupedAgreements).filter(type => groupedAgreements[type] && groupedAgreements[type].length > 0);
      if (availableTypes.length > 0 && !availableTypes.includes(activeTab)) {
        setActiveTab(availableTypes[0]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to load agreements';
      setError(errorMessage);
      showError(errorMessage, 8000);
      console.error('Error loading agreements:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshAgreementsCount = () => {
    if (Sgl.refreshPendingAgreementsCount) {
      Sgl.refreshPendingAgreementsCount();
    }
  };

  const handleTabSwitch = (agreementType) => {
    dismissNotification();
    setActiveTab(agreementType);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSignAgreement = (agreement) => {
    if(agreement.userRequest){
      window.referrer = window.location.pathname+'#my_agreements'
      navigate(`${agreement.userRequest.accept_url}`)
    }else{
      setSelectedAgreement(agreement)
      setDisplayAgreement(true)
    }
  }

  const closeAgreementDetail = () => {
    setSelectedAgreement(null)
    setDisplayAgreement(false)
    loadAgreements(true)
    refreshAgreementsCount() 
  }

  const closeAgreementDetailWithoutUpdate = () => {
    setSelectedAgreement(null)
    setDisplayAgreement(false)
  }

  // mobile responsive layout
  const renderAgreementCard = (agreement) => {
    const formatSignedDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    // Mobile layout
    if (isMobile) {
      return (
        <div key={agreement.agreement_id || agreement.sgl_id} className="agreement-card">
          {/* Title Section */}
          <div className="agreement-title-section">
            <h4 className="agreement-title">{agreement.title}</h4>
            <span className="version-badge">
              v{agreement.version_number}
            </span>
          </div>
          
          {/* Signed Date */}
          {agreement.signed_at && (
            <div className="signed-date">
              Signed on {formatSignedDate(agreement.signed_at)}
            </div>
          )}
          
          {/* Description */}
          {agreement.description && (
            <div 
              className="agreement-description"
              dangerouslySetInnerHTML={{ __html: agreement.description }}
            />
          )}
          
          {/* Button */}
          <div className="agreement-actions-header">
            {agreement.pdf_url ? (
              <a 
                href={agreement.pdf_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-view-agreement-header"
              >
                View Agreement
              </a>
            ) : (
              <span className="no-pdf-available-header">PDF not available</span>
            )}
          </div>
        </div>
      );
    }

    // Desktop layout
    return (
      <div key={agreement.agreement_id || agreement.sgl_id} className="agreement-card">
          <div className="agreement-header">
            <div className="agreement-title-section">
              <h4 className="agreement-title">{agreement.title}</h4>
              <span className="version-badge">
                v{agreement.version_number}
              </span>
            </div>
            <div className="agreement-actions-header">
              {agreement.pdf_url ? (
                <a 
                  href={agreement.pdf_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-view-agreement-header"
                >
                  View Agreement
                </a>
              ) : (
                <span className="no-pdf-available-header">PDF not available</span>
              )}
            </div>
          </div>
        
        {agreement.signed_at && (
          <div className="signed-date">
            Signed on {formatSignedDate(agreement.signed_at)}
          </div>
        )}
        
        {agreement.description && (
          <div 
            className="agreement-description"
            dangerouslySetInnerHTML={{ __html: agreement.description }}
          />
        )}
      
      </div>
    );
  };

  const renderPendingAgreementCard = (agreement) => {
    // Mobile layout: Title, Description, Button
    if (isMobile) {
      return (
        <div key={agreement.agreement_id || agreement.sgl_id} className="agreement-card">
          {/* Title Section */}
          <div className="agreement-title-section">
            <h4 className="agreement-title">{agreement.title}</h4>
            <span className="version-badge">
              v{agreement.version}
            </span>
          </div>
          
          {/* Description */}
          {agreement.description && (
            <div 
              className="agreement-description"
              dangerouslySetInnerHTML={{ __html: agreement.description }}
            />
          )}
          
          {/* Button */}
          <div className="agreement-actions-header">
            <a 
              className="btn-view-agreement-header"
              onClick={() => handleSignAgreement(agreement)}
            >
              Sign Agreement
            </a>
          </div>
        </div>
      );
    }

    // Desktop layout: Original structure
    return (
      <div key={agreement.agreement_id || agreement.sgl_id} className="agreement-card">
        <div className="agreement-header">
          <div className="agreement-title-section">
            <h4 className="agreement-title">{agreement.title}</h4>
            <span className="version-badge">
              v{agreement.version}
            </span>
          </div>
          <div className="agreement-actions-header">
            <a 
              className="btn-view-agreement-header"
              onClick={() => handleSignAgreement(agreement)}
            >
              Sign Agreement
            </a>
          </div>
        </div>
        
          {agreement.description && (
            <div 
              className="agreement-description"
              dangerouslySetInnerHTML={{ __html: agreement.description }}
            />
          )}
      </div>
    );
  };

  const renderTabContent = (agreementType) => {
    const typeAgreements = agreements[agreementType] || [];
    
    if (typeAgreements.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            {agreementType === 'signed_agreements' && '📄'}
            {/* {agreementType === 'pending_agreements' && '⏳'} */}
          </div>
          <div className="empty-state-text">
            No {agreementTypeLabels[agreementType]?.toLowerCase() || 'agreements'}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="agreements-list">
          {typeAgreements.map((agreement, idx) => (
            <div key={`${agreement.agreement_id}-${idx}`}>
              {agreementType === 'pending_agreements'
                ? renderPendingAgreementCard(agreement)
                : renderAgreementCard(agreement)}
            </div>
          ))}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="my-agreements">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your agreements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-agreements">
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={() => loadAgreements(true)} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // const availableAgreementTypes = Object.keys(agreements).filter(type => agreements[type].length > 0);
  const availableAgreementTypes = Object.keys(agreements);
  const totalAgreements = Object.values(agreements).flat().length;

  if (totalAgreements === 0) {
    return (
      <div className="my-agreements">
        <div className="agreements-header">
          <h2>My Agreements</h2>
        </div>
        <InlineNotification 
          notification={notification} 
          onDismiss={dismissNotification} 
        />
        <div className="no-agreements">
          <div className="no-agreements-icon">📋</div>
          <h3>No agreements found</h3>
          <p>You don't have any agreements at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    {
      displayAgreement ? null:
      <div className="my-agreements">
        <div className="agreements-header">
          <h2>My Agreements</h2>
        </div>

        <InlineNotification 
          notification={notification} 
          onDismiss={dismissNotification} 
        />

        <div className="agreements-tabs">
          <div className="scroll-indicator left" ref={leftIndicatorRef}></div>
          <div className="tab-scroll-container" ref={tabContainerRef}>
            <div className="tab-buttons">
              {availableAgreementTypes.map(agreementType => {
                return (
                  <button
                    key={agreementType}
                    className={`tab-button ${activeTab === agreementType ? 'active' : ''}`}
                    onClick={() => handleTabSwitch(agreementType)}
                  >
                    <span className="tab-label">{agreementTypeLabels[agreementType]}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="scroll-indicator right" ref={rightIndicatorRef}></div>
        </div>

        <div className="tab-content">
          {renderTabContent(activeTab)}
        </div>
      </div>
    }

    {
      displayAgreement && selectedAgreement ?
      <AgreementDetail agreement={selectedAgreement} handleClose={closeAgreementDetail} closeAgreementDetailWithoutUpdate={closeAgreementDetailWithoutUpdate}/> : null
    }
    </>
  );
};

export default MyAgreements;